import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Helper to safely format currency
 */
const getCurrency = (settings) => {
  if (settings?.currency === '₹') return 'INR';
  return settings?.currency || 'INR';
};

/**
 * Helper to load an image asynchronously
 */
const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

/**
 * Generates a PDF invoice/receipt for a specific transaction
 * @param {Object} transaction - The transaction object
 * @param {Object} business - The business details
 * @param {Object} customer - The customer details
 */
export const generateTransactionInvoice = async (transaction, business, customer) => {
  const doc = new jsPDF();
  
  // Premium Theme Colors
  const primaryColor = [180, 84, 246]; // Purple/Violet
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];
  const lightGrayBg = [248, 249, 250];
  const borderColor = [230, 230, 230];

  // Helper function for left-bordered card
  const drawCard = (x, y, w, h, title, details) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(x, y, w, h, 3, 3, 'FD');
    
    doc.setFillColor(...primaryColor);
    doc.rect(x + 4, y + 4, 2, 8, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...primaryColor);
    doc.text(title, x + 10, y + 10);
    
    doc.setTextColor(...darkText);
    let currentY = y + 16;
    details.forEach(line => {
      if (!line.text) return;
      if (line.bold) doc.setFont('helvetica', 'bold');
      else doc.setFont('helvetica', 'normal');
      doc.setFontSize(line.size || 10);
      
      let textX = x + 8;
      if (line.highlight) {
        doc.setFont('helvetica', 'bold');
        doc.text(line.highlight, textX, currentY);
        textX += doc.getTextWidth(line.highlight);
        doc.setFont('helvetica', 'normal');
      }
      
      // Wrap long text
      const splitText = doc.splitTextToSize(line.text, w - (textX - x) - 4);
      doc.text(splitText, textX, currentY);
      currentY += (splitText.length * 4) + (line.space || 1);
    });
  };

  // --- HEADER ---
  let logoOffset = 0;
  if (business?.assets?.logoUrl) {
    try {
      const img = await loadImage(business.assets.logoUrl);
      if (img) {
        doc.addImage(img, 'PNG', 14, 15, 20, 20);
        logoOffset = 25;
      }
    } catch (e) {}
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...darkText);
  const rawBizName = (business?.name || business?.businessName || 'Your Business').toUpperCase();
  const splitBizName = doc.splitTextToSize(rawBizName, 110);
  doc.text(splitBizName, 14 + logoOffset, 22);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...primaryColor);
  const taglineY = 22 + (splitBizName.length * 6);
  doc.text('OFFICIAL PAYMENT RECEIPT', 14 + logoOffset, taglineY);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...primaryColor);
  doc.text('RECEIPT', 196, 22, { align: 'right' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...grayText);
  doc.text(`#${transaction._id?.substring(0, 8).toUpperCase() || 'N/A'}`, 196, 28, { align: 'right' });

  // --- CARDS ---
  const businessDetails = [
    { text: business?.name || business?.businessName || 'Your Business', bold: true, size: 11 },
    { text: business?.address?.street || 'Local Street' },
    { text: business?.address?.city ? `${business.address.city}, ${business.address.state || ''}` : 'City, State' },
    { text: business?.address?.country || 'India' },
    { text: ' ', space: 2 },
    { highlight: 'CONTACT: ', text: business?.contact?.phone || 'N/A' }
  ];
  if (business?.contact?.email) businessDetails.push({ highlight: 'EMAIL: ', text: business.contact.email });
  if (business?.gstNumber) businessDetails.push({ highlight: 'GSTIN: ', text: business.gstNumber });

  const customerDetails = [
    { text: customer?.name || 'Customer Name', bold: true, size: 11 },
    { text: customer?.phone ? `Phone: ${customer.phone}` : '' },
    { text: customer?.email ? `Email: ${customer.email}` : '' },
    { text: customer?.address || '' }
  ];

  drawCard(14, 40, 85, 55, 'ISSUED BY', businessDetails);
  drawCard(105, 40, 85, 55, 'RECEIVED FROM', customerDetails);

  // --- METADATA ROW ---
  doc.setDrawColor(...borderColor);
  doc.line(14, 105, 196, 105);
  doc.line(14, 125, 196, 125);

  const drawMeta = (x, y, title, val) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    doc.text(title, x, y);
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    doc.text(val, x, y + 5);
  };

  drawMeta(14, 112, 'RECEIPT DATE', new Date(transaction.createdAt || Date.now()).toLocaleDateString('en-GB'));
  drawMeta(60, 112, 'TIME', new Date(transaction.createdAt || Date.now()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}));
  drawMeta(105, 112, 'PAYMENT MODE', transaction.reference?.paymentMode?.toUpperCase() || 'CASH');
  drawMeta(150, 112, 'STATUS', transaction.status?.toUpperCase() || 'COMPLETED');

  // --- TABLE ---
  const tableData = [
    [
      transaction.type === 'credit' ? 'Gave (Udhaar)' : 'Got (Payment)',
      transaction.description || 'N/A',
      `${getCurrency(business?.settings)} ${transaction.amount?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || '0.00'}`
    ]
  ];

  doc.autoTable({
    startY: 135,
    head: [['TYPE', 'DESCRIPTION', 'AMOUNT']],
    body: tableData,
    theme: 'plain',
    headStyles: { 
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 9,
      textColor: darkText
    },
    columnStyles: {
      2: { halign: 'right', fontStyle: 'bold' }
    },
    didDrawPage: function(data) {
      doc.setDrawColor(...borderColor);
      doc.line(14, data.cursor.y, 196, data.cursor.y);
    }
  });

  const finalY = doc.lastAutoTable.finalY || 160;

  // Purple Total Bar
  const totalY = finalY + 15;
  doc.setFillColor(...primaryColor);
  doc.roundedRect(110, totalY - 5, 86, 12, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL AMOUNT', 115, totalY + 3);
  doc.text(`${getCurrency(business?.settings)} ${transaction.amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 191, totalY + 3, { align: 'right' });

  // --- FOOTER ---
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text('THIS IS A COMPUTER GENERATED RECEIPT. NO PHYSICAL SIGNATURE REQUIRED.', 105, 285, { align: 'center' });

  doc.save(`Receipt_${customer?.name?.replace(/\s+/g, '_') || 'Customer'}_${transaction._id?.substring(0, 6)}.pdf`);
};

/**
 * Generates a full Ledger PDF for a customer
 * @param {Array} transactions - Array of transactions
 * @param {Object} business - The business details
 * @param {Object} customer - The customer details
 */
export const generateLedgerInvoice = async (transactions, business, customer) => {
  const doc = new jsPDF();
  
  // Premium Theme Colors
  const primaryColor = [180, 84, 246]; // Purple/Violet
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...primaryColor);
  doc.text('CUSTOMER LEDGER', 14, 20);

  doc.setFontSize(16);
  doc.setTextColor(...darkText);
  doc.text(business?.name || business?.businessName || 'Khatabook', 14, 32);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`Customer: ${customer?.name}`, 14, 45);
  doc.text(`Phone: ${customer?.phone}`, 14, 51);
  doc.text(`Current Balance: Rs. ${customer?.currentBalance?.toLocaleString() || 0}`, 14, 57);

  doc.setTextColor(...darkText);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 140, 20);

  const tableData = transactions.map(t => [
    new Date(t.createdAt).toLocaleDateString(),
    t.description,
    t.type === 'credit' ? `Rs. ${t.amount.toLocaleString()}` : '-',
    t.type === 'payment' || t.type === 'debit' ? `Rs. ${t.amount.toLocaleString()}` : '-'
  ]);

  doc.autoTable({
    startY: 65,
    head: [['Date', 'Details', 'You Gave (Dr)', 'You Got (Cr)']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: primaryColor }
  });

  doc.save(`Ledger_${customer?.name?.replace(/\s+/g, '_')}.pdf`);
};

export const generateTaxInvoice = async (invoiceData, business, customer, includeGst, gstRate) => {
  const doc = new jsPDF();
  
  // Premium Theme Colors
  const primaryColor = [180, 84, 246]; // Purple/Violet
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];
  const lightGrayBg = [248, 249, 250];
  const borderColor = [230, 230, 230];

  // Helper function for left-bordered card
  const drawCard = (x, y, w, h, title, details) => {
    // Card Background
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(x, y, w, h, 3, 3, 'FD');
    
    // Left Accent Border
    doc.setFillColor(...primaryColor);
    doc.rect(x + 4, y + 4, 2, 8, 'F');
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...primaryColor);
    doc.text(title, x + 10, y + 10);
    
    // Details
    doc.setTextColor(...darkText);
    let currentY = y + 16;
    details.forEach(line => {
      if (!line.text) return;
      if (line.bold) doc.setFont('helvetica', 'bold');
      else doc.setFont('helvetica', 'normal');
      doc.setFontSize(line.size || 10);
      
      let textX = x + 8;
      if (line.highlight) {
        doc.setFont('helvetica', 'bold');
        doc.text(line.highlight, textX, currentY);
        textX += doc.getTextWidth(line.highlight);
        doc.setFont('helvetica', 'normal');
      }
      
      // Wrap long text
      const splitText = doc.splitTextToSize(line.text, w - (textX - x) - 4);
      doc.text(splitText, textX, currentY);
      currentY += (splitText.length * 4) + (line.space || 1);
    });
  };

  // --- HEADER ---
  
  // Try to add Business Logo
  let logoOffset = 0;
  if (business?.assets?.logoUrl) {
    try {
      const img = await loadImage(business.assets.logoUrl);
      if (img) {
        doc.addImage(img, 'PNG', 14, 15, 20, 20);
        logoOffset = 25;
      }
    } catch (e) {}
  }

  // Business Name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...darkText);
  const rawName = (business?.name || 'Your Business').toUpperCase();
  const splitName = doc.splitTextToSize(rawName, 110);
  doc.text(splitName, 14 + logoOffset, 22);
  
  // Tagline
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...primaryColor);
  const taxTaglineY = 22 + (splitName.length * 6);
  doc.text('OFFICIAL TAX INVOICE • BILLING DOCUMENT', 14 + logoOffset, taxTaglineY);
  // INVOICE Text (Right side)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...primaryColor);
  doc.text(includeGst ? 'TAX INVOICE' : 'INVOICE', 196, 22, { align: 'right' });
  
  // Invoice Number
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...grayText);
  doc.text(invoiceData.invoiceNumber || 'NEW', 196, 28, { align: 'right' });

  // --- ISSUED BY & BILL TO CARDS ---
  const businessDetails = [
    { text: business?.name || 'Your Business', bold: true, size: 11 },
    { text: business?.address?.street || 'Local Street' },
    { text: business?.address?.city ? `${business.address.city}, ${business.address.state || ''}` : 'City, State' },
    { text: business?.address?.country || 'India' },
    { text: ' ', space: 2 },
    { highlight: 'CONTACT: ', text: business?.contact?.phone || 'N/A' },
    { highlight: 'EMAIL: ', text: business?.contact?.email || 'N/A' },
  ];
  
  if (business?.gstNumber) {
    businessDetails.push({ highlight: 'GSTIN: ', text: business.gstNumber });
  }

  const customerDetails = [
    { text: customer?.name || 'Customer Name', bold: true, size: 11 },
    { text: customer?.phone ? `Phone: ${customer.phone}` : '' },
    { text: customer?.email ? `Email: ${customer.email}` : '' },
    { text: customer?.address || '' }
  ];

  drawCard(14, 40, 85, 55, 'ISSUED BY', businessDetails);
  drawCard(105, 40, 85, 55, 'BILL TO', customerDetails);

  // --- METADATA ROW ---
  doc.setDrawColor(...borderColor);
  doc.line(14, 105, 196, 105);
  doc.line(14, 125, 196, 125);

  const drawMeta = (x, y, title, val) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    doc.text(title, x, y);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    doc.text(val, x, y + 5);
  };

  drawMeta(14, 112, 'ISSUE DATE', new Date(invoiceData.issueDate || Date.now()).toLocaleDateString('en-GB'));
  drawMeta(60, 112, 'DUE DATE', invoiceData.dueDate ? new Date(invoiceData.dueDate).toLocaleDateString('en-GB') : 'Upon Receipt');
  drawMeta(105, 112, 'CURRENCY', getCurrency(business?.settings));
  drawMeta(150, 112, 'PAYMENT STATUS', 'PENDING');

  // --- PRELOAD ITEM IMAGES ---
  const itemImages = {};
  const hasImages = invoiceData.items.some(i => i.imageUrl);
  if (hasImages) {
    await Promise.all(invoiceData.items.map(async (item, index) => {
      if (item.imageUrl) {
        try {
          const img = await loadImage(item.imageUrl);
          if (img) itemImages[index] = img;
        } catch (e) {}
      }
    }));
  }

  // --- ITEMS TABLE ---
  const hasHsn = invoiceData.items.some(i => i.hsnCode && i.hsnCode.trim() !== '');
  
  const headRow = hasImages ? ['IMG'] : [];
  headRow.push('DESCRIPTION');
  if (hasHsn) headRow.push('HSN/SAC');
  headRow.push('QTY', 'PRICE');
  if (includeGst) headRow.push('CGST', 'SGST');
  headRow.push('AMOUNT');
  
  const head = [headRow];

  const body = invoiceData.items.map(item => {
    const row = [];
    if (hasImages) row.push(''); // Placeholder for image
    row.push(item.name);
    if (hasHsn) row.push(item.hsnCode || '-');
    row.push(item.quantity);

    if (includeGst) {
      const itemTotal = item.quantity * item.unitPrice;
      const cgst = (itemTotal * (gstRate / 2)) / 100;
      const sgst = (itemTotal * (gstRate / 2)) / 100;
      row.push(
        `${item.unitPrice.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${cgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${sgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${item.total.toLocaleString('en-IN', {minimumFractionDigits: 2})}`
      );
    } else {
      row.push(
        `${item.unitPrice.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${item.total.toLocaleString('en-IN', {minimumFractionDigits: 2})}`
      );
    }
    return row;
  });

  doc.autoTable({
    startY: 135,
    head: head,
    body: body,
    theme: 'plain',
    headStyles: { 
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 9,
      textColor: darkText
    },
    alternateRowStyles: {
      fillColor: [252, 252, 252]
    },
    columnStyles: {
      0: hasImages ? { cellWidth: 15, halign: 'center' } : { cellWidth: 70 },
      1: hasImages ? { cellWidth: 55 } : undefined,
      [headRow.length - 1]: { halign: 'right', fontStyle: 'bold' }
    },
    didDrawCell: function(data) {
      // Draw image in the first column if available
      if (hasImages && data.section === 'body' && data.column.index === 0) {
        const img = itemImages[data.row.index];
        if (img) {
          const imgSize = 10;
          const x = data.cell.x + (data.cell.width - imgSize) / 2;
          const y = data.cell.y + (data.cell.height - imgSize) / 2;
          doc.addImage(img, 'PNG', x, y, imgSize, imgSize);
        }
      }
    },
    didDrawPage: function(data) {
      // Top Border of table
      doc.setDrawColor(...borderColor);
      doc.line(14, data.cursor.y, 196, data.cursor.y);
    }
  });

  const finalY = doc.lastAutoTable.finalY || 160;

  // --- TOTALS SECTION ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...grayText);
  
  doc.text('SUBTOTAL', 120, finalY + 15);
  doc.setTextColor(...darkText);
  doc.text(`${getCurrency(business?.settings)} ${invoiceData.subtotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 15, { align: 'right' });

  if (includeGst) {
    doc.setTextColor(...grayText);
    doc.text(`GST (${gstRate}%)`, 120, finalY + 22);
    doc.setTextColor(...darkText);
    doc.text(`${getCurrency(business?.settings)} ${invoiceData.taxAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 22, { align: 'right' });
  }

  if (invoiceData.discountAmount > 0) {
    doc.setTextColor(...grayText);
    doc.text('DISCOUNT', 120, finalY + 29);
    doc.setTextColor(34, 197, 94); // Green
    doc.text(`- ${getCurrency(business?.settings)} ${invoiceData.discountAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 29, { align: 'right' });
  }

  // Purple Total Bar
  let totalY = finalY + 35;
  
  if (invoiceData.advanceAmount > 0) {
    doc.setTextColor(...grayText);
    doc.text('ADVANCE PAID', 120, finalY + 36);
    doc.setTextColor(34, 197, 94); // Green
    doc.text(`- ${getCurrency(business?.settings)} ${Number(invoiceData.advanceAmount).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 36, { align: 'right' });
    totalY += 10;
  }

  doc.setFillColor(...primaryColor);
  doc.roundedRect(110, totalY - 5, 86, 12, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text('BALANCE DUE', 115, totalY + 3);
  doc.text(`${getCurrency(business?.settings)} ${(invoiceData.balanceDue !== undefined ? invoiceData.balanceDue : invoiceData.grandTotal).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 191, totalY + 3, { align: 'right' });

  // --- TERMS ---
  const termsY = totalY + 30;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...primaryColor);
  doc.text('OFFICIAL SERVICE TERMS', 14, termsY);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  
  const customTerms = invoiceData.termsAndConditions 
    ? invoiceData.termsAndConditions 
    : '• Please pay within the due date to avoid late fees.\n• Intellectual Property: Assets remain property of business until full payment.\n• Governance: Disputes subject to local jurisdiction.';

  const splitTerms = doc.splitTextToSize(customTerms, 110);
  doc.text(splitTerms, 14, termsY + 6);

  // Calculate where QR codes should start based on terms height
  const qrStartY = Math.max(termsY + (splitTerms.length * 4) + 10, termsY + 28);

  // --- VERIFICATION QR CODE ---
  if (invoiceData.invoiceNumber) {
    try {
      const verifyUrl = `${window.location.origin}/verify/${invoiceData.invoiceNumber}`;
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(verifyUrl)}`;
      const qrImg = await loadImage(qrApiUrl);
      if (qrImg) {
        doc.addImage(qrImg, 'PNG', 170, termsY, 25, 25);
        doc.setFontSize(7);
        doc.setTextColor(...grayText);
        doc.text('Scan to Verify', 182.5, termsY + 28, { align: 'center' });
      }
    } catch (error) {
      console.warn("QR code generation failed", error);
    }
  }

  // --- UPI PAYMENT QR CODE ---
  if (business?.payment?.upiId && invoiceData.grandTotal > 0) {
    try {
      const bizName = business.name || business.businessName || 'Business';
      const upiUrl = `upi://pay?pa=${business.payment.upiId}&pn=${encodeURIComponent(bizName)}&am=${invoiceData.grandTotal}&cu=INR`;
      const upiQrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(upiUrl)}`;
      const upiImg = await loadImage(upiQrApiUrl);
      if (upiImg) {
        doc.addImage(upiImg, 'PNG', 140, termsY, 25, 25);
        doc.setFontSize(7);
        doc.setTextColor(34, 197, 94); // Green
        doc.text('Scan to Pay via UPI', 152.5, termsY + 28, { align: 'center' });
      }
    } catch (error) {
      console.warn("UPI QR code generation failed", error);
    }
  }

  // --- SIGNATURE / WATERMARK ---
  if (business?.assets?.signatureUrl) {
    try {
      const signatureImg = await loadImage(business.assets.signatureUrl);
      if (signatureImg) {
        // Place signature at bottom right, below the Terms and QR codes
        doc.addImage(signatureImg, 'PNG', 140, qrStartY, 40, 15); // Adjust dimensions as needed
        doc.setFontSize(7);
        doc.setTextColor(...darkText);
        doc.text('AUTHORIZED SIGNATORY', 160, qrStartY + 18, { align: 'center' });
      }
    } catch (error) {
      console.warn("Signature loading failed", error);
    }
  }

  // --- FOOTER ---
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text('THIS IS A COMPUTER GENERATED DOCUMENT.', 105, 285, { align: 'center' });

  doc.save(`Invoice_${customer?.name?.replace(/\s+/g, '_') || 'WalkIn'}.pdf`);
};
